import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Briefcase, CheckCircle2, Clock, AlertTriangle, Loader2, Plus, Layers,
  Users, BookOpen, ArrowRight, Pencil, Trash2, ChevronRight, Rocket, Download,
} from 'lucide-react';
import { SuiteHero } from '@/components/client/SuiteHero';
import { GuidedEmptyState } from '@/components/client/GuidedEmptyState';
import { SuiteImportWizard } from '@/components/client/SuiteImportWizard';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { OnboardingTemplateDialog } from '@/components/client/OnboardingTemplateDialog';
import { AssignOnboardingDialog } from '@/components/client/AssignOnboardingDialog';
import { bizDate } from '@/lib/dates';
import { DonutCard, ProgressCard, SEM } from '@/components/client/SuiteAnalytics';

/**
 * Sprint A2 — TalentStart real onboarding console.
 *
 * Tabs:
 *   - Dashboard:    KPIs + executive metrics
 *   - Active:       in-flight assignments with visual pipeline
 *   - Templates:    reusable position-based onboarding routes
 *
 * All data is real (onboarding_templates + onboarding_assignments). No mocks.
 */

const STATUS_TONE = {
  not_started: 'bg-muted/40 text-muted-foreground border-muted',
  in_progress: 'bg-warning/15 text-warning border-warning/30',
  completed: 'bg-success/15 text-success border-success/30',
  overdue: 'bg-destructive/15 text-destructive border-destructive/30',
};

const STAGE_TONE = {
  not_started: 'bg-muted/40 text-muted-foreground',
  in_progress: 'bg-warning/15 text-warning',
  completed: 'bg-success/15 text-success',
};

const fmtDate = (iso, lang = 'es') => bizDate(iso, lang);

function StageStepper({ stages, currentIdx }) {
  return (
    <ol className="flex items-center gap-1 overflow-x-auto py-1" data-testid="onboarding-stepper">
      {stages.map((s) => (
        <li key={s.idx} className="flex items-center gap-1 shrink-0">
          <div
            data-testid={`stage-pill-${s.idx}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${STAGE_TONE[s.status] || STAGE_TONE.not_started} ${
              s.idx === currentIdx ? 'ring-2 ring-primary' : ''
            }`}
          >
            <span className="font-bold tabular-nums">{s.idx + 1}</span>
            <span className="truncate max-w-[12rem]" title={s.name}>{s.name}</span>
            {s.status === 'completed' && <CheckCircle2 className="h-3 w-3" />}
          </div>
          {s.idx < stages.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
        </li>
      ))}
    </ol>
  );
}

function KpiCard({ icon: Icon, label, value, tone = 'primary', hint, testid }) {
  const tones = {
    primary: 'from-primary/15 to-primary/5 text-primary',
    success: 'from-success/15 to-success/5 text-success',
    warning: 'from-warning/15 to-warning/5 text-warning',
    destructive: 'from-destructive/15 to-destructive/5 text-destructive',
    info: 'from-info/15 to-info/5 text-info',
    muted: 'from-muted to-muted/40 text-muted-foreground',
  };
  return (
    <Card className="p-4 shadow-card" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
          <p className="text-2xl font-bold tabular-nums mt-0.5">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
        </div>
      </div>
    </Card>
  );
}

export default function ClientTalentStart() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [tplEditing, setTplEditing] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTpl, setAssignTpl] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importTplOpen, setImportTplOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, tpls, asgs, collabs, depts] = await Promise.all([
        clientApi.onboardingDashboard().catch(() => null),
        clientApi.onboardingListTemplates().catch(() => []),
        clientApi.onboardingListAssignments().catch(() => []),
        clientApi.listCollaborators().catch(() => []),
        clientApi.listDepartments().catch(() => []),
      ]);
      setDashboard(d);
      setTemplates(tpls);
      setAssignments(asgs);
      setCollaborators(collabs);
      setDepartments(depts);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const kpis = dashboard?.kpis || {};
  const overdueRows = dashboard?.overdue || [];
  const byDept = dashboard?.by_department || [];

  // Helpers
  const handleArchive = async (tid) => {
    if (!window.confirm(t('talentstart.template.archiveConfirm', { defaultValue: 'Archive this template? Active assignments are unaffected.' }))) return;
    try {
      await clientApi.onboardingArchiveTemplate(tid);
      toast.success(t('talentstart.template.archived', { defaultValue: 'Template archived.' }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archiveAssignment = async (a) => {
    if (!window.confirm(t('talentstart.assignment.archiveConfirm', { defaultValue: 'Archive this onboarding? It leaves the active view but its full history is preserved and it can be restored.' }))) return;
    try {
      await clientApi.onboardingArchiveAssignment(a.id);
      toast.success(t('talentstart.assignment.archived', { defaultValue: 'Onboarding archived.' }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return (
      <>
        <Topbar title={t('talentstart.title', { defaultValue: 'TalentStart' })}
                subtitle={t('talentstart.subtitle', { defaultValue: 'Onboarding & employee integration' })} />
        <div className="py-16 flex items-center justify-center text-muted-foreground" data-testid="talentstart-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={(
          <span className="inline-flex items-center gap-2">
            {t('talentstart.title', { defaultValue: 'TalentStart' })}
            <Badge className="bg-primary/15 text-primary border-primary/30">{t('suiteChip.talentstart')}</Badge>
          </span>
        )}
        subtitle={t('talentstart.subtitle', { defaultValue: 'Onboarding & employee integration' })}
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="talentstart-import-btn"
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => setImportOpen(true)}
            >
              <Download className="h-4 w-4" /> {lng?.startsWith('es') ? 'Importar asignaciones (Excel)' : 'Import assignments (Excel)'}
            </Button>
            <Button
              data-testid="talentstart-import-templates-btn"
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => setImportTplOpen(true)}
            >
              <Download className="h-4 w-4" /> {lng?.startsWith('es') ? 'Importar plantillas (Excel)' : 'Import templates (Excel)'}
            </Button>
            <Button
              data-testid="talentstart-new-template-btn"
              size="sm"
              className="bg-gradient-primary hover:opacity-90 gap-1.5"
              onClick={() => { setTplEditing(null); setTplDialogOpen(true); }}
            >
              <Plus className="h-4 w-4" /> {t('talentstart.newTemplate', { defaultValue: 'New template' })}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="client-talentstart">
        <SuiteHero suite="talentstart" icon={Rocket} />
        <Tabs defaultValue="dashboard">
          <TabsList data-testid="talentstart-tabs">
            <TabsTrigger value="dashboard" data-testid="talentstart-tab-dashboard">
              {t('talentstart.tabs.dashboard', { defaultValue: 'Dashboard' })}
            </TabsTrigger>
            <TabsTrigger value="active" data-testid="talentstart-tab-active">
              {t('talentstart.tabs.active', { defaultValue: 'Active onboardings' })}
              {assignments.filter((a) => a.status !== 'completed').length > 0 && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {assignments.filter((a) => a.status !== 'completed').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="talentstart-tab-templates">
              {t('talentstart.tabs.templates', { defaultValue: 'Templates' })}
              {templates.length > 0 && (
                <Badge variant="outline" className="ml-2 text-xs">{templates.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ===================== DASHBOARD TAB ===================== */}
          <TabsContent value="dashboard" className="space-y-4 mt-4">
            {/* Founder UAT 2026-06 — executive visual analytics (real data) */}
            {((kpis.active ?? 0) + (kpis.completed ?? 0) + (kpis.overdue ?? 0)) > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ts-analytics-row">
                <DonutCard
                  title={t('talentstart.analytics.statusTitle', { defaultValue: 'Onboardings por estado' })}
                  testid="ts-chart-status"
                  accent={SEM.blue}
                  data={[
                    { name: t('talentstart.kpi.active', { defaultValue: 'Activos' }), value: kpis.active ?? 0, color: SEM.orange },
                    { name: t('talentstart.kpi.completed', { defaultValue: 'Completados' }), value: kpis.completed ?? 0, color: SEM.green },
                    { name: t('talentstart.kpi.overdue', { defaultValue: 'Atrasados' }), value: kpis.overdue ?? 0, color: SEM.red },
                  ]}
                  emptyText={t('talentstart.analytics.empty', { defaultValue: 'Sin onboardings todavía.' })}
                />
                <ProgressCard
                  title={t('talentstart.analytics.progressTitle', { defaultValue: 'Avance promedio del onboarding' })}
                  testid="ts-chart-progress"
                  accent={SEM.green}
                  total={100}
                  rows={[
                    { label: t('talentstart.kpi.avgProgress', { defaultValue: 'Progreso promedio' }), value: kpis.avg_progress_pct ?? 0, color: SEM.green },
                  ]}
                  emptyText={t('talentstart.analytics.empty', { defaultValue: 'Sin onboardings todavía.' })}
                />
              </div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="talentstart-kpis">
              <KpiCard
                icon={Briefcase} tone="primary" testid="ts-kpi-active"
                label={t('talentstart.kpi.active', { defaultValue: 'Active' })}
                value={kpis.active ?? 0}
              />
              <KpiCard
                icon={CheckCircle2} tone="success" testid="ts-kpi-completed"
                label={t('talentstart.kpi.completed', { defaultValue: 'Completed' })}
                value={kpis.completed ?? 0}
              />
              <KpiCard
                icon={AlertTriangle} tone="destructive" testid="ts-kpi-overdue"
                label={t('talentstart.kpi.overdue', { defaultValue: 'Overdue' })}
                value={kpis.overdue ?? 0}
              />
              <KpiCard
                icon={Layers} tone="info" testid="ts-kpi-templates"
                label={t('talentstart.kpi.templates', { defaultValue: 'Templates' })}
                value={kpis.templates ?? 0}
              />
              <KpiCard
                icon={Clock} tone="warning" testid="ts-kpi-avg-progress"
                label={t('talentstart.kpi.avgProgress', { defaultValue: 'Avg. progress' })}
                value={`${kpis.avg_progress_pct ?? 0}%`}
              />
              <KpiCard
                icon={Clock} tone="muted" testid="ts-kpi-avg-duration"
                label={t('talentstart.kpi.avgDuration', { defaultValue: 'Avg. duration' })}
                value={`${kpis.avg_duration_days ?? 0}d`}
              />
              <KpiCard
                icon={BookOpen} tone="primary" testid="ts-kpi-hours-est"
                label={t('talentstart.kpi.hoursEstimated', { defaultValue: 'Hours estimated' })}
                value={kpis.hours_estimated_total ?? 0}
              />
              <KpiCard
                icon={BookOpen} tone="success" testid="ts-kpi-hours-done"
                label={t('talentstart.kpi.hoursCompleted', { defaultValue: 'Hours completed' })}
                value={kpis.hours_completed_total ?? 0}
                hint={t('talentstart.kpi.hoursRemainingHint', { remaining: kpis.hours_remaining_total ?? 0, defaultValue: '{{remaining}}h remaining' })}
              />
            </div>

            {/* Overdue */}
            <Card className="p-5 shadow-card" data-testid="talentstart-overdue-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-base inline-flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  {t('talentstart.overdue.title', { defaultValue: 'Employees overdue' })}
                </h3>
                <Badge variant="outline">{overdueRows.length}</Badge>
              </div>
              {overdueRows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center" data-testid="talentstart-overdue-empty">
                  {t('talentstart.overdue.empty', { defaultValue: 'No employees are currently overdue.' })}
                </p>
              ) : (
                <ul className="space-y-2" data-testid="talentstart-overdue-list">
                  {overdueRows.map((row) => {
                    const u = collaborators.find((c) => c.id === row.user_id);
                    return (
                      <li key={row.id} className="flex items-center justify-between p-2 rounded-md border border-destructive/20 bg-destructive/5"
                          data-testid={`talentstart-overdue-row-${row.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{u?.name || row.user_id}</p>
                          <p className="text-xs text-muted-foreground">
                            {(row.user_position || row.position_snapshot)} · {t('talentstart.overdue.dueLabel', { defaultValue: 'Due' })} {fmtDate(row.due_at, lng)} · {row.progress_pct}%
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/client/talentstart/${row.id}`)}
                                data-testid={`talentstart-overdue-open-${row.id}`}>
                          {t('common.open', { defaultValue: 'Open' })} <ArrowRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            {/* By department */}
            <Card className="p-5 shadow-card" data-testid="talentstart-by-dept-card">
              <h3 className="font-semibold text-base mb-3">
                {t('talentstart.byDept.title', { defaultValue: 'Department participation' })}
              </h3>
              {byDept.length === 0 ? (
                <GuidedEmptyState
                  compact
                  dataTestId="talentstart-by-dept-empty"
                  icon={Users}
                  title={t('guidedEmpty.talentstart.title')}
                  why={t('guidedEmpty.talentstart.why')}
                  steps={[1, 2, 3, 4].map((n) => t(`guidedEmpty.talentstart.step${n}`))}
                />
              ) : (
                <Table data-testid="talentstart-by-dept-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('talentstart.byDept.dept', { defaultValue: 'Department' })}</TableHead>
                      <TableHead className="text-right">{t('talentstart.byDept.stages', { defaultValue: 'Stages' })}</TableHead>
                      <TableHead className="text-right">{t('talentstart.byDept.completed', { defaultValue: 'Completed' })}</TableHead>
                      <TableHead className="text-right">{t('talentstart.byDept.hours', { defaultValue: 'Hours' })}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byDept.map((r) => (
                      <TableRow key={r.department_id} data-testid={`talentstart-by-dept-row-${r.department_id}`}>
                        <TableCell>{r.department_name}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.stages}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.completed}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.hours.toFixed(1)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          {/* ===================== ACTIVE TAB ===================== */}
          <TabsContent value="active" className="space-y-4 mt-4">
            <Card className="p-5 shadow-card" data-testid="talentstart-active-card">
              {assignments.length === 0 ? (
                <div className="py-10 text-center text-muted-foreground" data-testid="talentstart-active-empty">
                  <Briefcase className="h-10 w-10 opacity-30 mx-auto mb-2" />
                  <p className="text-sm">
                    {t('talentstart.active.empty', { defaultValue: 'No onboardings yet. Create a template and assign it to a collaborator.' })}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('talentstart.active.user', { defaultValue: 'Collaborator' })}</TableHead>
                      <TableHead>{t('talentstart.active.position', { defaultValue: 'Position' })}</TableHead>
                      <TableHead>{t('talentstart.active.template', { defaultValue: 'Onboarding template' })}</TableHead>
                      <TableHead>{t('talentstart.active.status', { defaultValue: 'Status' })}</TableHead>
                      <TableHead>{t('talentstart.active.progress', { defaultValue: 'Progress' })}</TableHead>
                      <TableHead>{t('talentstart.active.due', { defaultValue: 'Due' })}</TableHead>
                      <TableHead>{t('talentstart.active.pipeline', { defaultValue: 'Pipeline' })}</TableHead>
                      <TableHead className="text-right">{t('common.actions', { defaultValue: 'Actions' })}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map((a) => (
                      <TableRow key={a.id} data-testid={`talentstart-active-row-${a.id}`}>
                        <TableCell className="font-medium">{a.user_name}</TableCell>
                        <TableCell data-testid={`talentstart-active-position-${a.id}`}>{a.user_position || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground" title={a.name_snapshot}>{a.name_snapshot || a.position_snapshot}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_TONE[a.status]}>
                            {t(`talentstart.status.${a.status}`, { defaultValue: a.status })}
                          </Badge>
                        </TableCell>
                        <TableCell className="tabular-nums">{a.progress_pct}%</TableCell>
                        <TableCell className="tabular-nums">{fmtDate(a.due_at, lng)}</TableCell>
                        <TableCell className="max-w-[320px]">
                          <StageStepper stages={a.stages} currentIdx={a.current_stage_idx} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1.5">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/client/talentstart/${a.id}`)}
                                    data-testid={`talentstart-active-open-${a.id}`}>
                              {t('common.open', { defaultValue: 'Open' })}
                            </Button>
                            <Button size="sm" variant="ghost" className="text-muted-foreground"
                                    onClick={() => archiveAssignment(a)}
                                    data-testid={`talentstart-archive-${a.id}`}>
                              {t('talentstart.archiveAction', { defaultValue: 'Archivar' })}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          {/* ===================== TEMPLATES TAB ===================== */}
          <TabsContent value="templates" className="space-y-4 mt-4">
            <Card className="p-5 shadow-card" data-testid="talentstart-templates-card">
              {templates.length === 0 ? (
                <div className="py-10 text-center text-muted-foreground" data-testid="talentstart-templates-empty">
                  <Layers className="h-10 w-10 opacity-30 mx-auto mb-2" />
                  <p className="text-sm mb-4">
                    {t('talentstart.templates.empty', { defaultValue: 'No templates yet. Build the first onboarding route for a role.' })}
                  </p>
                  <Button size="sm" onClick={() => { setTplEditing(null); setTplDialogOpen(true); }}
                          data-testid="talentstart-templates-empty-cta">
                    <Plus className="h-4 w-4 mr-1" /> {t('talentstart.newTemplate', { defaultValue: 'New template' })}
                  </Button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="talentstart-templates-grid">
                  {templates.map((tpl) => (
                    <Card key={tpl.id} className="p-4 border" data-testid={`talentstart-template-${tpl.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="font-semibold truncate" title={tpl.name}>{tpl.name}</p>
                          <p className="text-xs text-muted-foreground">
                            <Users className="inline h-3 w-3 mr-1" />{tpl.position}
                            {' · '}
                            <Layers className="inline h-3 w-3 mr-1" />
                            {tpl.stages?.length || 0} {t('talentstart.templates.stagesLabel', { defaultValue: 'stages' })}
                          </p>
                        </div>
                      </div>
                      {tpl.description && (
                        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{tpl.description}</p>
                      )}
                      <StageStepper stages={(tpl.stages || []).map((s) => ({ ...s, status: 'not_started' }))} currentIdx={-1} />
                      <div className="mt-3 flex items-center gap-2">
                        <Button
                          size="sm" variant="default"
                          onClick={() => { setAssignTpl(tpl); setAssignDialogOpen(true); }}
                          data-testid={`talentstart-template-assign-${tpl.id}`}
                        >
                          {t('talentstart.templates.assign', { defaultValue: 'Assign' })}
                        </Button>
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => { setTplEditing(tpl); setTplDialogOpen(true); }}
                          data-testid={`talentstart-template-edit-${tpl.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => handleArchive(tpl.id)}
                          data-testid={`talentstart-template-archive-${tpl.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <OnboardingTemplateDialog
        open={tplDialogOpen}
        onOpenChange={setTplDialogOpen}
        template={tplEditing}
        departments={departments}
        collaborators={collaborators}
        onSaved={load}
      />
      <AssignOnboardingDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        template={assignTpl}
        collaborators={collaborators.filter((c) => c.role === 'evaluated' || !c.role)}
        onAssigned={load}
      />
      <SuiteImportWizard suite="talentstart" open={importOpen}
                         onClose={() => setImportOpen(false)} onImported={load} />
      <SuiteImportWizard suite="talentstart_templates" open={importTplOpen}
                         onClose={() => setImportTplOpen(false)} onImported={load} />
    </>
  );
}
